import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import 'reactflow/dist/style.css';
import './App.css';
import './Builder.css';

// Auth & Layout
import { AuthProvider, useAuth } from './context/AuthContext';
import { SessionProvider, useSession } from './context/SessionContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { Layout } from './components/Layout';
import { ProtectedRoute, PublicRoute } from './components/ProtectedRoute';

// Pages
import { LoginPage } from './pages/auth';
import { ThreediumCallbackPage } from './pages/auth/ThreediumCallbackPage';
import { AdminDashboard, AdminUsers, AdminProjectsPage, AdminTierLimits, AdminModels } from './pages/admin';
import { ProfilePage } from './pages/profile';
import { LandingPage } from './pages/landing';
import { DesignerPage } from './pages/designer';
import { UsagePage } from './pages/usage';

// Custom components
import Header from './components/Header';
import ErrorBoundary from './components/ErrorBoundary';
import { BuilderPage } from './components/builder';
import { AgentDesigner } from './components/designer';
import { useAgentConfigStore } from './stores/agentConfigStore';
import { PublishModal } from './components/PublishModal';

/**
 * Main App Content (Builder + Designer)
 * 
 * This is the integrated view where Builder and Designer work together.
 * Users can switch between modes while working on a project.
 * 
 * The AgentDesigner component is the SINGLE SOURCE OF TRUTH for the designer UI.
 * It's used both here (embedded) and in the standalone DesignerPage.
 */
function MainApp({ initialMode = 'builder', projectId: propProjectId }) {
  // Get project ID from URL params (for /experience/:projectId route)
  const { projectId: urlProjectId } = useParams();
  const projectIdToLoad = propProjectId || urlProjectId;

  // Session readiness — AgentDesigner talks to the session backend, so defer mount
  const { isReady: sessionReady, isCloudMode } = useSession();

  // Initialize agent config store
  const { initialize, initialized } = useAgentConfigStore();

  useEffect(() => {
    if (!initialized) {
      initialize();
    }
  }, [initialize, initialized]);

  // Mode state: 'builder' or 'agent-designer'
  const [activeMode, setActiveMode] = useState(initialMode);
  // Builder state for header visibility ('start', 'prompt', 'workspace')
  const [builderState, setBuilderState] = useState('start');
  // Use shared theme context
  const { theme, setTheme } = useTheme();
  // Builder chat sidebar visibility
  const [builderChatSidebarOpen, setBuilderChatSidebarOpen] = useState(true);
  // Builder view mode: 'preview' or 'code'
  const [builderViewMode, setBuilderViewMode] = useState('preview');
  const [deviceMode, setDeviceMode] = useState('desktop');
  
  // Publish modal state
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [currentProject, setCurrentProject] = useState(null);
  
  // Handle publish button click
  const handlePublishClick = useCallback(() => {
    if (currentProject) {
      setShowPublishModal(true);
    }
  }, [currentProject]);
  
  // Handle project info updates from BuilderPage
  const handleProjectChange = useCallback((project) => {
    setCurrentProject(project);
  }, []);

  return (
    <div className="App main-app">
      <Header
        activeMode={activeMode}
        onModeChange={setActiveMode}
        showLeftRibbon={builderState === 'workspace' || activeMode === 'agent-designer'}
        theme={theme}
        onThemeChange={setTheme}
        chatSidebarOpen={builderChatSidebarOpen}
        onToggleChatSidebar={() => setBuilderChatSidebarOpen(prev => !prev)}
        viewMode={builderViewMode}
        onViewModeChange={setBuilderViewMode}
        deviceMode={deviceMode}
        onDeviceModeChange={setDeviceMode}
        onPublishClick={handlePublishClick}
        project={currentProject}
      />

      {/* Builder Mode */}
      <div
        className="mode-container builder-mode"
        style={{ display: activeMode === 'builder' ? 'block' : 'none' }}
      >
        <BuilderPage
          onStateChange={setBuilderState}
          chatSidebarOpen={builderChatSidebarOpen}
          viewMode={builderViewMode}
          deviceMode={deviceMode}
          initialProjectId={projectIdToLoad}
          onProjectChange={handleProjectChange}
        />
      </div>

      {/* Agent Designer Mode - Only mount once session backend is reachable */}
      {activeMode === 'agent-designer' && (!isCloudMode || sessionReady) && (
        <div className="mode-container agent-designer-mode" style={{ display: 'contents' }}>
          <ErrorBoundary name="Agent Designer">
            <AgentDesigner 
              standalone={true}
              visible={true}
            />
          </ErrorBoundary>
        </div>
      )}
      
      {/* Publish Modal */}
      <PublishModal
        projectId={currentProject?.aws_project_id || currentProject?.id}
        projectName={currentProject?.display_name || currentProject?.name}
        isOpen={showPublishModal}
        onClose={() => setShowPublishModal(false)}
        initialData={{
          subdomain: currentProject?.subdomain,
          visibility: currentProject?.visibility,
          is_published: currentProject?.is_published,
          preview_url: currentProject?.preview_url
        }}
      />
    </div>
  );
}

// Root Route - Shows LandingPage for unauthenticated users, redirects to /dashboard for authenticated
function RootRoute() {
  const { isAuthenticated, isLoading } = useAuth();

  // Show loading spinner while checking auth
  if (isLoading) {
    return (
      <div className="auth-loading">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Show landing page for unauthenticated users
  if (!isAuthenticated) {
    return <LandingPage />;
  }

  // Redirect authenticated users to /dashboard
  return <Navigate to="/dashboard" replace />;
}

// Global unhandled rejection handler (C-05) + source map suppression (H-22)
function GlobalRejectionHandler({ children }) {
  useEffect(() => {
    const handler = (event) => {
      const reason = event?.reason;
      const msg = reason?.message ?? (typeof reason === 'string' ? reason : String(reason ?? ''));
      if (msg.includes('.map') || msg.toLowerCase().includes('sourcemap')) return;
      console.error('Unhandled promise rejection:', reason);
    };
    window.addEventListener('unhandledrejection', handler);
    return () => window.removeEventListener('unhandledrejection', handler);
  }, []);
  return children;
}

// Routes wrapped in ErrorBoundary with pathname reset (C-02)
function AppRoutes() {
  const location = useLocation();
  return (
    <ErrorBoundary name="App" resetKey={location.pathname}>
      <Routes>
            {/* Public Auth Routes */}
            <Route path="/login" element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            } />
            {/* Threedium OAuth callback */}
            <Route path="/auth/threedium/callback" element={
              <ThreediumCallbackPage />
            } />

            {/* Protected Routes with Layout */}
            <Route path="/profile" element={
              <ProtectedRoute>
                <Layout pageTitle="Profile & Settings">
                  <ProfilePage />
                </Layout>
              </ProtectedRoute>
            } />
            
            {/* Settings - alias for profile */}
            <Route path="/settings" element={
              <ProtectedRoute>
                <Layout pageTitle="Settings">
                  <ProfilePage />
                </Layout>
              </ProtectedRoute>
            } />

            {/* Usage Analytics */}
            <Route path="/usage" element={
              <ProtectedRoute>
                <Layout pageTitle="Usage Analytics">
                  <UsagePage />
                </Layout>
              </ProtectedRoute>
            } />

            {/* Admin Routes */}
            <Route path="/admin" element={
              <ProtectedRoute requireAdmin>
                <Layout pageTitle="Admin Dashboard">
                  <AdminDashboard />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/users" element={
              <ProtectedRoute requireAdmin>
                <Layout pageTitle="User Management">
                  <AdminUsers />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/projects" element={
              <ProtectedRoute requireAdmin>
                <Layout pageTitle="All Projects">
                  <AdminProjectsPage />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/tier-limits" element={
              <ProtectedRoute requireAdmin>
                <Layout pageTitle="Tier Limits">
                  <AdminTierLimits />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/models" element={
              <ProtectedRoute requireAdmin>
                <Layout pageTitle="Model Management">
                  <AdminModels />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/admin/settings" element={
              <ProtectedRoute requireAdmin>
                <Layout pageTitle="Admin Settings">
                  <ProfilePage />
                </Layout>
              </ProtectedRoute>
            } />

            {/* Dashboard - explicit route for the main builder */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <MainApp />
              </ProtectedRoute>
            } />

            {/* Experience/Project - opens specific project in builder */}
            <Route path="/experience/:projectId" element={
              <ProtectedRoute>
                <MainApp />
              </ProtectedRoute>
            } />

            {/* Agent Designer - standalone page for creating/managing workflow presets */}
            <Route path="/designer" element={
              <ProtectedRoute>
                <DesignerPage />
              </ProtectedRoute>
            } />

            {/* Landing Page for unauthenticated users, redirects to /dashboard for authenticated */}
            <Route path="/" element={<RootRoute />} />

            {/* Fallback - redirect to dashboard */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </ErrorBoundary>
  );
}

// Root App with Router
function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <SessionProvider>
            <GlobalRejectionHandler>
              <AppRoutes />
            </GlobalRejectionHandler>
          </SessionProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
